'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  ArrowRight,
  Feather,
  Shield,
  Users,
  BookOpen,
  Eye,
  GitMerge,
  Scale,
  Layers,
  Star,
  CheckCircle2,
  Mic,
  Search,
} from 'lucide-react';

const rotatingWords = ['forgotten', 'suppressed', 'undocumented', 'erased', 'untold'];

const wallTopics = [
  { label: 'Great Zimbabwe', coverage: 87, filled: true },
  { label: 'Chimurenga Wars', coverage: 72, filled: true },
  { label: 'Rozvi Empire', coverage: 18, filled: false },
  { label: 'Colonial Land Acts', coverage: 65, filled: true },
  { label: 'Matabeleland Uprisings', coverage: 24, filled: false },
  { label: 'Mutapa Kingdom', coverage: 58, filled: true },
  { label: 'Liberation Struggle', coverage: 91, filled: true },
  { label: 'Early Iron Age', coverage: 11, filled: false },
  { label: 'Oral Law Traditions', coverage: 29, filled: false },
  { label: 'Independence 1980', coverage: 83, filled: true },
  { label: 'Ndebele Formation', coverage: 32, filled: false },
  { label: 'Colonial Admin', coverage: 61, filled: true },
];

export default function GriottLandingPage() {
  const [wordIndex, setWordIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setWordIndex((i) => (i + 1) % rotatingWords.length);
        setVisible(true);
      }, 300);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      {/* ── HERO ── */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: '94vh', backgroundColor: 'var(--background)' }}
      >
        {/* Atmospheric background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 70% 20%, #CC000012 0%, transparent 55%), radial-gradient(ellipse 50% 80% at 10% 90%, #99000010 0%, transparent 50%), radial-gradient(ellipse 40% 40% at 90% 80%, #2C5E3B08 0%, transparent 50%)',
          }}
        />
        {/* Grain texture */}
        <div
          className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.06'/%3E%3C/svg%3E")`,
          }}
        />

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 pt-32 pb-20 lg:pt-40 lg:pb-28">
          <div className="grid lg:grid-cols-[1fr_440px] xl:grid-cols-[1fr_500px] gap-16 lg:gap-20 items-center">

            {/* Left — copy */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium mb-10 tracking-wide"
                style={{
                  borderColor: '#CC000044',
                  backgroundColor: '#CC00000D',
                  color: 'var(--primary)',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: 'var(--primary)' }} />
                Community-curated · Human-moderated · Open archive
              </div>

              <h1
                className="text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.05] tracking-tight mb-6"
                style={{ color: 'var(--foreground)' }}
              >
                African history
                <br />
                <span className="relative inline-block">
                  <span
                    className="transition-all duration-300"
                    style={{
                      color: 'var(--primary)',
                      opacity: visible ? 1 : 0,
                      transform: visible ? 'translateY(0)' : 'translateY(8px)',
                      display: 'inline-block',
                    }}
                  >
                    {rotatingWords[wordIndex]}
                  </span>
                </span>
                <br />
                no longer.
              </h1>

              <p className="text-lg lg:text-xl leading-relaxed mb-4 max-w-xl" style={{ color: 'var(--muted-foreground)' }}>
                Griott is a living archive of Zimbabwean and African history — built by the community, verified by human curators, and open to everyone.
              </p>
              <p className="text-base leading-relaxed mb-10 max-w-xl" style={{ color: 'var(--muted-foreground)', opacity: 0.8 }}>
                Oral traditions, firsthand accounts, and sourced records are preserved with full transparency about what we know, what we don't, and where the gaps remain.
              </p>

              <div className="flex flex-wrap gap-3 mb-14">
                <Link
                  href="/sign-up-login"
                  className="flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-sm transition-all duration-150 active:scale-95 shadow-sm"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  Join Griott — it's free
                  <ArrowRight size={16} />
                </Link>
                <Link
                  href="/contributions"
                  className="flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-sm border transition-all duration-150 active:scale-95"
                  style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--card)' }}
                >
                  <BookOpen size={16} />
                  Explore the archive
                </Link>
              </div>

              <div className="flex flex-wrap gap-5">
                {[
                  { icon: Feather, label: 'Oral tradition respected' },
                  { icon: Shield, label: 'Human moderation pipeline' },
                  { icon: Users, label: 'Community corroboration' },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: '#CC000018' }}>
                      <item.icon size={13} style={{ color: 'var(--primary)' }} />
                    </div>
                    <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — bento cards */}
            <div className="hidden lg:grid grid-cols-2 gap-3 auto-rows-min">
              {/* Mission card */}
              <div
                className="col-span-2 rounded-2xl border p-6 relative overflow-hidden"
                style={{ backgroundColor: 'var(--card)', borderColor: '#CC000033', boxShadow: '0 2px 12px rgba(204,0,0,0.08)' }}
              >
                <div
                  className="absolute top-0 right-0 w-32 h-32 rounded-full pointer-events-none"
                  style={{ background: 'radial-gradient(circle, #CC000018 0%, transparent 70%)', transform: 'translate(30%, -30%)' }}
                />
                <div className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
                  Our mission
                </div>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--foreground)' }}>
                  To document the documented gaps in African history — and fill them with verified community knowledge before they disappear.
                </p>
              </div>

              {/* Stat — contributors */}
              <div
                className="rounded-2xl border p-5 flex flex-col justify-between"
                style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}
              >
                <div className="text-3xl font-bold tabular-nums mb-1" style={{ color: 'var(--primary)' }}>312</div>
                <div className="text-xs leading-snug" style={{ color: 'var(--muted-foreground)' }}>Active contributors across the archive</div>
              </div>

              {/* Stat — stories */}
              <div
                className="rounded-2xl border p-5 flex flex-col justify-between"
                style={{ backgroundColor: '#99000014', borderColor: '#99000033' }}
              >
                <div className="text-3xl font-bold tabular-nums mb-1" style={{ color: '#990000' }}>1,847</div>
                <div className="text-xs leading-snug" style={{ color: 'var(--muted-foreground)' }}>Published stories</div>
              </div>

              {/* How it works */}
              <div
                className="col-span-2 rounded-2xl border p-5"
                style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}
              >
                <div className="text-xs font-mono uppercase tracking-widest mb-4" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                  How stories work
                </div>
                <div className="flex items-start gap-3">
                  {[
                    { step: '01', label: 'Submit', desc: 'Write your account, cite your sources' },
                    { step: '02', label: 'Review', desc: 'Human curators verify and classify' },
                    { step: '03', label: 'Publish', desc: 'Joins the permanent archive' },
                  ].map((s, i) => (
                    <div key={s.step} className="flex-1 flex items-start gap-2">
                      <span className="text-xs font-mono mt-0.5 shrink-0" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
                        {s.step}
                      </span>
                      <div>
                        <div className="text-xs font-semibold mb-0.5" style={{ color: 'var(--foreground)' }}>{s.label}</div>
                        <div className="text-xs leading-snug" style={{ color: 'var(--muted-foreground)' }}>{s.desc}</div>
                      </div>
                      {i < 2 && <ArrowRight size={12} className="mt-1 shrink-0 opacity-30" style={{ color: 'var(--muted-foreground)' }} />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent, var(--background))' }}
        />
      </section>

      {/* ── VALUE PROPOSITION ── */}
      <section className="py-20 border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="mb-12">
            <div className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
              Why Griott exists
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold leading-tight" style={{ color: 'var(--foreground)' }}>
              History isn't just what's written down.
              <br />
              <span style={{ color: 'var(--primary)' }}>It's what's remembered.</span>
            </h2>
          </div>

          {/* Asymmetric bento */}
          <div className="grid lg:grid-cols-[2fr_1fr_1fr] gap-4">
            {/* Wide card */}
            <div
              className="rounded-2xl border p-7 flex flex-col justify-between relative overflow-hidden"
              style={{ backgroundColor: '#7B6FA314', borderColor: '#7B6FA333', minHeight: '260px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
            >
              <div
                className="absolute bottom-0 right-0 w-40 h-40 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, #7B6FA322 0%, transparent 70%)', transform: 'translate(30%, 30%)' }}
              />
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: '#7B6FA322' }}>
                <Eye size={20} style={{ color: '#7B6FA3' }} />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--foreground)' }}>Radical transparency</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  Every entry is labelled: oral tradition, firsthand account, or sourced claim. Readers always know what kind of evidence they're reading.
                </p>
              </div>
            </div>

            {/* Two stacked */}
            <div className="flex flex-col gap-4">
              <div
                className="rounded-2xl border p-6 flex flex-col justify-between flex-1"
                style={{ backgroundColor: '#00808014', borderColor: '#00808033', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#00808022' }}>
                  <GitMerge size={18} style={{ color: '#008080' }} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Community corroboration</h3>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                    Contributors can corroborate or dispute each other's accounts. Contested history is shown as contested — not silently removed.
                  </p>
                </div>
              </div>
              <div
                className="rounded-2xl border p-6 flex flex-col justify-between flex-1"
                style={{ backgroundColor: '#CC000014', borderColor: '#CC000033', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: '#CC000022' }}>
                  <Scale size={18} style={{ color: 'var(--primary)' }} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-1.5" style={{ color: 'var(--foreground)' }}>Human moderation</h3>
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                    No algorithm decides what's true. A trained curator reviews every submission before it enters the archive.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA card */}
            <div
              className="rounded-2xl border p-7 flex flex-col justify-between relative overflow-hidden"
              style={{
                background: 'linear-gradient(145deg, #CC000018 0%, #99000010 50%, #FAF7F2 100%)',
                borderColor: '#CC000044',
                boxShadow: '0 2px 12px rgba(204,0,0,0.08)',
              }}
            >
              <div>
                <div className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
                  28 documented gaps
                </div>
                <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--muted-foreground)' }}>
                  These are topics where the historical record is thin. Your knowledge — oral or sourced — could fill one.
                </p>
              </div>
              <Link
                href="/sign-up-login"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-150 active:scale-95 w-full shadow-sm"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                Fill a gap
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── THE WALL METAPHOR ── */}
      <section className="py-20 border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-16 items-center mb-14">
            {/* Left — explanation */}
            <div>
              <div className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
                The Wall
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold leading-tight mb-5" style={{ color: 'var(--foreground)' }}>
                A map of what we know —
                <br />
                <span style={{ color: 'var(--primary)' }}>and what we're still missing.</span>
              </h2>
              <p className="text-base leading-relaxed mb-5" style={{ color: 'var(--muted-foreground)' }}>
                The Wall is Griott's visual index of African history. Each stone represents a topic. Filled stones are documented. Dashed gaps are where the record is thin — and where your contribution matters most.
              </p>
              <p className="text-sm leading-relaxed mb-8" style={{ color: 'var(--muted-foreground)', opacity: 0.85 }}>
                Unlike a database or a search index, the Wall makes absence visible. You can see at a glance which eras, regions, and events are under-documented — and choose to fill them.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  href="/the-wall"
                  className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-150 active:scale-95"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  <Layers size={15} />
                  Explore the Wall
                </Link>
                <Link
                  href="/sign-up-login"
                  className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm border transition-all duration-150 active:scale-95"
                  style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--card)' }}
                >
                  Add to a gap
                  <ArrowRight size={15} />
                </Link>
              </div>
            </div>

            {/* Right — Wall legend */}
            <div
              className="rounded-2xl border p-6"
              style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
            >
              <div className="text-xs font-mono uppercase tracking-widest mb-5" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                Coverage at a glance
              </div>
              <div className="grid grid-cols-3 gap-2 mb-5">
                {wallTopics.map((topic) => (
                  <div
                    key={topic.label}
                    className={`relative rounded-lg p-2.5 min-h-[64px] flex flex-col justify-between transition-all duration-200 ${
                      topic.filled ? 'wall-block-filled' : 'wall-block-gap'
                    }`}
                  >
                    <span
                      className="text-xs font-medium leading-tight"
                      style={{ color: topic.filled ? '#FFFFFFCC' : 'var(--muted-foreground)' }}
                    >
                      {topic.label}
                    </span>
                    <div className="flex items-center gap-1 mt-1.5">
                      <div
                        className="h-1 rounded-full flex-1"
                        style={{ backgroundColor: topic.filled ? '#FFFFFF22' : 'var(--muted)' }}
                      >
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${topic.coverage}%`,
                            backgroundColor: topic.filled ? '#FFFFFFAA' : '#CC000055',
                          }}
                        />
                      </div>
                      <span
                        className="text-xs tabular-nums font-mono shrink-0"
                        style={{ color: topic.filled ? '#FFFFFF88' : 'var(--muted-foreground)', fontFamily: 'var(--font-mono)', fontSize: '10px' }}
                      >
                        {topic.coverage}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded wall-block-filled" />
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Covered (≥50%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded wall-block-gap" />
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Gap — needs contributions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ROLES ── */}
      <section className="py-20 border-t" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--secondary)' }}>
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="mb-12 text-center max-w-2xl mx-auto">
            <div className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
              Who builds the archive
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold leading-tight" style={{ color: 'var(--foreground)' }}>
              Two roles. One mission.
            </h2>
            <p className="text-base mt-4 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              Griott runs on the collaboration between Contributors who bring knowledge and Historians who verify it.
            </p>
          </div>

          {/* Asymmetric role cards */}
          <div className="grid lg:grid-cols-[1fr_1fr] gap-6 mb-10">
            {/* Contributor */}
            <div
              className="rounded-2xl border p-8 relative overflow-hidden"
              style={{ backgroundColor: 'var(--card)', borderColor: '#CC000033', boxShadow: '0 2px 12px rgba(204,0,0,0.06)' }}
            >
              <div
                className="absolute top-0 right-0 w-48 h-48 pointer-events-none"
                style={{ background: 'radial-gradient(circle, #CC000010 0%, transparent 70%)', transform: 'translate(20%, -20%)' }}
              />
              <div className="flex items-start gap-4 mb-6">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: '#CC000018' }}
                >
                  <Feather size={22} style={{ color: 'var(--primary)' }} />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest mb-1" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
                    Role
                  </div>
                  <h3 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Contributor</h3>
                </div>
              </div>
              <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--muted-foreground)' }}>
                You have knowledge — oral accounts passed down through family, firsthand memories, or sourced research. As a Contributor, you write and submit stories to the archive. You can also corroborate or dispute existing accounts, building a richer, more honest record.
              </p>
              <div className="space-y-2.5 mb-8">
                {[
                  'Submit oral traditions, firsthand accounts, and sourced records',
                  'Corroborate or dispute other contributors\' accounts',
                  'Build a public profile and earn trust tiers over time',
                  'Attach citations, audio recordings, and media',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <CheckCircle2 size={15} className="mt-0.5 shrink-0" style={{ color: 'var(--primary)' }} />
                    <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{item}</span>
                  </div>
                ))}
              </div>
              <Link
                href="/sign-up-login"
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-150 active:scale-95 w-full justify-center"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                Join as a Contributor
                <ArrowRight size={15} />
              </Link>
            </div>

            {/* Historian */}
            <div
              className="rounded-2xl border p-8 relative overflow-hidden"
              style={{ backgroundColor: 'var(--card)', borderColor: '#7B6FA333', boxShadow: '0 2px 12px rgba(123,111,163,0.06)' }}
            >
              <div
                className="absolute top-0 right-0 w-48 h-48 pointer-events-none"
                style={{ background: 'radial-gradient(circle, #7B6FA310 0%, transparent 70%)', transform: 'translate(20%, -20%)' }}
              />
              <div className="flex items-start gap-4 mb-6">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: '#7B6FA318' }}
                >
                  <BookOpen size={22} style={{ color: '#7B6FA3' }} />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest mb-1" style={{ color: '#7B6FA3', fontFamily: 'var(--font-mono)' }}>
                    Role
                  </div>
                  <h3 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Historian</h3>
                </div>
              </div>
              <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--muted-foreground)' }}>
                You have domain expertise — academic training, archival experience, or deep community knowledge. As a Historian, you review submitted contributions, verify claims, and give each story a verdict: corroborated, needs more information, or disputed.
              </p>
              <div className="space-y-2.5 mb-8">
                {[
                  'Review and verify submitted contributions',
                  'Issue corroboration, dispute, or needs-more-info verdicts',
                  'Access the Historian Review Panel and dashboard',
                  'Shape the quality and integrity of the archive',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <CheckCircle2 size={15} className="mt-0.5 shrink-0" style={{ color: '#7B6FA3' }} />
                    <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{item}</span>
                  </div>
                ))}
              </div>
              <Link
                href="/verification-request"
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-150 active:scale-95 w-full justify-center border"
                style={{ borderColor: '#7B6FA344', color: '#7B6FA3', backgroundColor: '#7B6FA308' }}
              >
                Apply as a Historian
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>

          {/* Trust tiers explainer */}
          <div
            className="rounded-2xl border p-7"
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}
          >
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="lg:w-64 shrink-0">
                <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                  Contributor tiers
                </div>
                <h4 className="text-lg font-bold" style={{ color: 'var(--foreground)' }}>Earn trust over time</h4>
                <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
                  Your reputation grows with each verified contribution.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 flex-1">
                {[
                  { tier: 'Newcomer', desc: 'Just joined — building your first contributions', color: '#888888' },
                  { tier: 'Trusted', desc: 'Consistent, verified contributions', color: '#008080' },
                  { tier: 'Elder', desc: 'Deep expertise, community-recognised', color: '#7B6FA3' },
                  { tier: 'Historian', desc: 'Verified domain expert with review access', color: '#CC0000' },
                ].map((t) => (
                  <div
                    key={t.tier}
                    className="flex items-start gap-3 rounded-xl border px-4 py-3 flex-1 min-w-[160px]"
                    style={{ borderColor: `${t.color}33`, backgroundColor: `${t.color}0A` }}
                  >
                    <Star size={14} className="mt-0.5 shrink-0" style={{ color: t.color }} />
                    <div>
                      <div className="text-xs font-semibold" style={{ color: t.color }}>{t.tier}</div>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{t.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-20 border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div className="mb-12">
            <div className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
              The process
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold leading-tight" style={{ color: 'var(--foreground)' }}>
              From memory to archive —
              <br />
              <span style={{ color: 'var(--primary)' }}>with full transparency.</span>
            </h2>
          </div>

          {/* Process — asymmetric bento */}
          <div className="grid lg:grid-cols-[1fr_1fr_1fr_1fr] gap-4">
            {[
              {
                step: '01',
                icon: Mic,
                title: 'Submit your account',
                body: 'Write your story — oral tradition, firsthand memory, or sourced research. Attach citations, audio, or media. Choose to publish under your name or anonymously.',
                accent: '#CC0000',
                bg: '#CC000010',
                border: '#CC000030',
              },
              {
                step: '02',
                icon: Search,
                title: 'Curator review',
                body: 'A trained human curator reads your submission, checks citations, and classifies the evidence type. No algorithm decides what\'s true.',
                accent: '#F39C12',
                bg: '#F39C1210',
                border: '#F39C1230',
              },
              {
                step: '03',
                icon: Users,
                title: 'Community corroboration',
                body: 'Other contributors can add supporting accounts or raise disputes. Contested history is shown as contested — with all perspectives visible.',
                accent: '#008080',
                bg: '#00808010',
                border: '#00808030',
              },
              {
                step: '04',
                icon: Layers,
                title: 'Permanent archive',
                body: 'Approved stories join the Wall. They\'re searchable, citable, and permanently preserved — with full provenance and evidence labels.',
                accent: '#7B6FA3',
                bg: '#7B6FA310',
                border: '#7B6FA330',
              },
            ].map((s) => (
              <div
                key={s.step}
                className="rounded-2xl border p-6 flex flex-col relative overflow-hidden"
                style={{ backgroundColor: s.bg, borderColor: s.border, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              >
                <div
                  className="absolute top-0 right-0 w-20 h-20 rounded-full pointer-events-none"
                  style={{ background: `radial-gradient(circle, ${s.accent}18 0%, transparent 70%)`, transform: 'translate(30%, -30%)' }}
                />
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-xs font-mono" style={{ color: s.accent, fontFamily: 'var(--font-mono)' }}>{s.step}</span>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.accent}22` }}>
                    <s.icon size={16} style={{ color: s.accent }} />
                  </div>
                </div>
                <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--foreground)' }}>{s.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SIGN-UP CTA ── */}
      <section className="py-24 border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
          <div
            className="rounded-3xl border p-10 lg:p-16 relative overflow-hidden text-center"
            style={{
              background: 'linear-gradient(135deg, #CC000018 0%, #99000010 40%, #FAF7F2 100%)',
              borderColor: '#CC000044',
              boxShadow: '0 4px 24px rgba(204,0,0,0.10)',
            }}
          >
            <div
              className="absolute top-0 left-1/2 w-96 h-96 pointer-events-none"
              style={{ background: 'radial-gradient(circle, #CC000012 0%, transparent 70%)', transform: 'translate(-50%, -40%)' }}
            />
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium mb-8 tracking-wide"
              style={{ borderColor: '#CC000044', backgroundColor: '#CC00000D', color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--primary)' }} />
              Free to join · No paywall on the archive
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight mb-5" style={{ color: 'var(--foreground)' }}>
              Your knowledge belongs
              <br />
              <span style={{ color: 'var(--primary)' }}>in the archive.</span>
            </h2>
            <p className="text-lg leading-relaxed mb-10 max-w-xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
              Whether you have oral accounts, archival sources, or firsthand memory — Griott is built for you. Join 312 contributors already preserving African history.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/sign-up-login"
                className="flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all duration-150 active:scale-95 shadow-md"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                Create your free account
                <ArrowRight size={18} />
              </Link>
              <Link
                href="/contributions"
                className="flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base border transition-all duration-150 active:scale-95"
                style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--card)' }}
              >
                <BookOpen size={18} />
                Browse the archive first
              </Link>
            </div>
            <p className="text-xs mt-8" style={{ color: 'var(--muted-foreground)', opacity: 0.7 }}>
              No credit card required · Read everything without signing in · Contribute with an account
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
